import Delta from "quill-delta";

/**
 * Merging the two copies of a clinic note (Quill delta in a .json envelope)
 * for the two-way bucket sync, instead of one copy replacing the other.
 *
 * There is no common ancestor to diff against (RDS keeps hashes of the last
 * agreed version, not its text), so the merge starts from the UNION of both
 * copies: text only RDS has and text only Supabase has are both kept, and
 * where the copies differ in formatting Supabase's formatting is used. The
 * operator then drops whatever should not survive, change by change, on
 * the redlined page.
 *
 * changes: [{ id, kind, ops }]
 *   kind "same"     -- text both copies hold (ops = that text)
 *   kind "rds"      -- text only RDS's copy holds
 *   kind "supabase" -- text only Supabase's copy holds
 *   kind "format"   -- one paragraph, same words, different formatting
 *                      (ops = RDS's, alt = Supabase's)
 * choices: { [id]: boolean } -- keep that text / use Supabase's formatting.
 */

export function opsOf(doc) {
  if (!doc || typeof doc !== "object") return null;
  if (Array.isArray(doc.delta?.ops)) return doc.delta.ops;
  if (Array.isArray(doc.content?.ops)) return doc.content.ops;
  if (Array.isArray(doc.ops)) return doc.ops;
  return null;
}

export const isNoteDocument = (doc) => opsOf(doc) !== null;

// Longest-common-subsequence diff of two string arrays, as runs of
// {count, added?, removed?} (the shape of the `diff` package's diffArrays,
// which is ESM-only and cannot load under the test runner). Notes have at
// most a few thousand paragraphs, so the table stays small.
export function diffArrays(fullA, fullB) {
  // Two copies of a note share most of it: strip the common head and tail
  // first, so the table only covers the region that actually differs.
  let head = 0;
  while (head < fullA.length && head < fullB.length && fullA[head] === fullB[head]) head += 1;
  let tail = 0;
  while (tail < fullA.length - head && tail < fullB.length - head
         && fullA[fullA.length - 1 - tail] === fullB[fullB.length - 1 - tail]) tail += 1;
  const a = fullA.slice(head, fullA.length - tail);
  const b = fullB.slice(head, fullB.length - tail);
  const wrap = (middle) => {
    const out = [];
    if (head) out.push({ count: head, added: false, removed: false });
    middle.forEach((p) => {
      const last = out[out.length - 1];
      if (last && last.added === p.added && last.removed === p.removed) last.count += p.count;
      else out.push(p);
    });
    if (tail) {
      const last = out[out.length - 1];
      if (last && !last.added && !last.removed) last.count += tail;
      else out.push({ count: tail, added: false, removed: false });
    }
    return out;
  };
  return wrap(lcsDiff(a, b));
}

function lcsDiff(a, b) {
  const n = a.length;
  const m = b.length;
  if (!n || !m) {
    return [
      ...(n ? [{ count: n, added: false, removed: true }] : []),
      ...(m ? [{ count: m, added: true, removed: false }] : []),
    ];
  }
  const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const parts = [];
  const push = (kind) => {
    const last = parts[parts.length - 1];
    if (last && last.kind === kind) last.count += 1;
    else parts.push({ kind, count: 1 });
  };
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) { push("same"); i += 1; j += 1; }
    else if (j < m && (i >= n || lcs[i][j + 1] >= lcs[i + 1][j])) { push("added"); j += 1; }
    else { push("removed"); i += 1; }
  }
  return parts.map((p) => ({ count: p.count, added: p.kind === "added", removed: p.kind === "removed" }));
}

// One entry per paragraph: its ops including the closing newline (which
// carries the paragraph's own format: header, list...).
function paragraphs(ops) {
  const out = [];
  // Rebuilt op by op so equal text compares equal: insert() drops empty
  // attribute objects and push() joins neighbouring runs.
  const doc = new Delta();
  (ops || []).forEach((o) => { if (o.insert !== undefined) doc.insert(o.insert, o.attributes); });
  doc.eachLine((line, attrs) => {
    const nl = Object.keys(attrs || {}).length ? { insert: "\n", attributes: attrs } : { insert: "\n" };
    out.push(line.ops.concat([nl]));
  });
  return out;
}

const plainOf = (ops) => ops.map((o) => (typeof o.insert === "string" ? o.insert : JSON.stringify(o.insert))).join("");

/**
 * Paragraph-level comparison (like a line diff): a paragraph is the same,
 * only in RDS, only in Supabase, or the same words formatted differently.
 * Consecutive paragraphs one copy alone holds form one change.
 */
export function buildChanges(rdsOps, supabaseOps) {
  const a = paragraphs(rdsOps);
  const b = paragraphs(supabaseOps);
  const parts = diffArrays(a.map((p) => JSON.stringify(p)), b.map((p) => JSON.stringify(p)));
  const changes = [];
  let n = 0;
  let ia = 0;
  let ib = 0;
  let pendingRds = [];
  let pendingSb = [];
  const add = (kind, ops, extra = {}) => {
    const last = changes[changes.length - 1];
    if (kind === "same" && last?.kind === "same") { last.ops = last.ops.concat(ops); return; }
    changes.push({ id: `${kind === "same" ? "s" : "c"}${n++}`, kind, ops, ...extra });
  };
  const flush = () => {
    // Same words, different formatting, paragraph for paragraph.
    if (pendingRds.length && pendingRds.length === pendingSb.length
        && pendingRds.every((p, i) => plainOf(p) === plainOf(pendingSb[i]))) {
      pendingRds.forEach((p, i) => add("format", p, { alt: pendingSb[i] }));
    } else {
      if (pendingRds.length) add("rds", pendingRds.flat());
      if (pendingSb.length) add("supabase", pendingSb.flat());
    }
    pendingRds = [];
    pendingSb = [];
  };
  parts.forEach((part) => {
    if (part.removed) { pendingRds = pendingRds.concat(a.slice(ia, ia + part.count)); ia += part.count; return; }
    if (part.added) { pendingSb = pendingSb.concat(b.slice(ib, ib + part.count)); ib += part.count; return; }
    flush();
    add("same", a.slice(ia, ia + part.count).flat());
    ia += part.count;
    ib += part.count;
  });
  flush();
  return changes;
}

// Union: keep everything from both copies, Supabase's formatting.
export function defaultChoices(changes) {
  const out = {};
  changes.forEach((c) => { if (c.kind !== "same") out[c.id] = true; });
  return out;
}

export function mergedOps(changes, choices) {
  const out = new Delta();
  changes.forEach((c) => {
    if (c.kind === "same") c.ops.forEach((o) => out.push({ ...o }));
    else if (c.kind === "format") (choices[c.id] ? c.alt : c.ops).forEach((o) => out.push({ ...o }));
    else if (choices[c.id]) c.ops.forEach((o) => out.push({ ...o }));
  });
  // A Quill document always ends with a newline.
  const text = out.ops.length ? out.ops[out.ops.length - 1].insert : "";
  if (typeof text !== "string" || !text.endsWith("\n")) out.insert("\n");
  return out.ops;
}

// The merged .json: the newer copy's envelope (sections, template keys...)
// with the merged ops where the ops live, stamped now.
export function mergedDocument(rdsDoc, supabaseDoc, changes, choices, now = new Date()) {
  const stamp = (d) => Date.parse(d?.lastUpdated || "") || 0;
  const base = stamp(rdsDoc) > stamp(supabaseDoc) ? rdsDoc : supabaseDoc;
  const ops = mergedOps(changes, choices);
  const doc = { ...base, lastUpdated: now.toISOString() };
  if (Array.isArray(base.delta?.ops)) doc.delta = { ...base.delta, ops };
  else if (Array.isArray(base.content?.ops)) doc.content = { ...base.content, ops };
  else doc.ops = ops;
  return doc;
}

// ---------------------------------------------------------------------------
// The redlined page: what the merged note will contain, each change marked.
// ---------------------------------------------------------------------------

export const MARK = {
  rds: { background: "#dbeafe" },          // only in RDS, kept (blue)
  supabase: { background: "#dcfce7" },     // only in Supabase, kept (green)
  format: { background: "#fef9c3" },       // formatting differs (yellow)
  dropped: { background: "#fee2e2", strike: true, color: "red" },
};

const EMBED_LABEL = { rds: "only in RDS", supabase: "only in Supabase" };

const FOLD = { color: "#94a3b8", italic: true };
const foldLine = (n) => [
  { insert: `⋯ ${n} unchanged paragraph${n === 1 ? "" : "s"} ⋯`, attributes: FOLD },
  { insert: "\n" },
];

// The paragraphs of an unchanged stretch to draw: `context` of them next to
// each neighbouring change, the rest folded into one "⋯ N unchanged ⋯" line.
function foldSame(ops, context, before, after) {
  const paras = paragraphs(ops);
  const head = before ? context : 0;
  const tail = after ? context : 0;
  // Folding a single paragraph away saves nothing.
  if (paras.length <= head + tail + 1) return ops;
  return [
    ...paras.slice(0, head).flat(),
    ...foldLine(paras.length - head - tail),
    ...paras.slice(paras.length - tail).flat(),
  ];
}

/**
 * Delta to render read-only, plus where each change sits in it
 * ([{ id, start, end }]) so a click on the page can toggle that change.
 *
 * context: a number folds unchanged text down to that many paragraphs
 * around each change, so the page shows only what is merged, changed or
 * added; leave it out to draw the whole note.
 */
export function redline(changes, choices, { context } = {}) {
  const out = new Delta();
  const ranges = [];
  changes.forEach((c, i) => {
    const start = out.length();
    if (c.kind === "same") {
      const ops = typeof context === "number" ? foldSame(c.ops, context, i > 0, i < changes.length - 1) : c.ops;
      ops.forEach((o) => out.push({ ...o }));
      return;
    }
    let mark;
    let ops = c.ops;
    if (c.kind === "format") {
      // The text stays either way; only whose formatting it carries changes.
      mark = MARK.format;
      ops = choices[c.id] ? c.alt : c.ops;
    } else {
      mark = choices[c.id] ? MARK[c.kind] : MARK.dropped;
    }
    ops.forEach((o) => {
      if (typeof o.insert === "string") {
        out.push({ insert: o.insert, attributes: { ...(o.attributes || {}), ...mark } });
      } else {
        // An embed (vitals / ROS grid) cannot be highlighted: say what it is.
        const label = choices[c.id] ? `▼ ${EMBED_LABEL[c.kind] || "changed"}` : `▼ dropped (${EMBED_LABEL[c.kind] || "changed"})`;
        out.push({ insert: label, attributes: { ...mark, bold: true } });
        out.push({ ...o });
      }
    });
    ranges.push({ id: c.id, start, end: out.length() });
  });
  return { ops: out.ops, ranges };
}

export function changeAt(ranges, index) {
  return ranges.find((r) => index >= r.start && index < r.end) || null;
}

export function previewText(ops, max = 80) {
  const text = (ops || []).map((o) => (typeof o.insert === "string" ? o.insert : "[grid]")).join("")
    .replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text || "(line break)";
}
